import { adalApiFetch } from '../../../adalConfig';

export const getTraceabilityLots = async (path, params, abortController) => {
    const response = await adalApiFetch(fetch, process.env.REACT_APP_BASE_URL + path + '?' + new URLSearchParams(params), {
        signal: abortController?.signal
    });
    if (!response.ok) {
        throw new Error(`Lot search failed (${response.status})`);
    }
    const data = await response.json();
    if (!Array.isArray(data)) {
        throw new Error('Lot search returned an invalid response');
    }
    return [...new Set(data.filter(lot => typeof lot === 'string' && lot.trim()))];
};

export const mapWithConcurrency = async (items, action, concurrency = 3, signal) => {
    const results = new Array(items.length);
    let nextIndex = 0;
    await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, async () => {
        while (nextIndex < items.length) {
            if (signal?.aborted) {
                throw new DOMException('Request cancelled', 'AbortError');
            }
            const index = nextIndex++;
            results[index] = await action(items[index], index);
        }
    }));
    return results;
};

export const mergeLotResponses = (path, responses) => {
    if (path === '/Reports/GetCustomers') {
        const customers = new Map();
        responses.forEach(response => response.data.forEach(row => {
            const previous = customers.get(row.customer.id);
            if (previous) {
                previous.lsnShipped.push(...row.lsnShipped);
                previous.lsnUnShipped.push(...row.lsnUnShipped);
            } else {
                customers.set(row.customer.id, {
                    ...row, lsnShipped: [...row.lsnShipped], lsnUnShipped: [...row.lsnUnShipped]
                });
            }
        }));
        return { data: [...customers.values()] };
    }
    if (path === '/Reports/GetCustomerSalesData') {
        const orders = new Map();
        responses.forEach(response => response.orderData.forEach(row => {
            const key = JSON.stringify([row.salesOrderMYOBID, row.shipmentMYOBID]);
            const previous = orders.get(key);
            if (previous) {
                previous.lines.push(...row.lines);
            } else {
                orders.set(key, { ...row, lines: [...row.lines] });
            }
        }));
        const orderData = [...orders.values()].map(row => ({
            ...row,
            quantity: row.lines.reduce((total, line) => total + line.quantity, 0),
            totalCost: row.lines.reduce((total, line) => total + line.quantity * line.price, 0)
        }));
        return {
            orderData,
            quantity: orderData.reduce((total, row) => total + row.quantity, 0),
            totalCost: orderData.reduce((total, row) => total + row.totalCost, 0)
        };
    }
    if (path === '/Reports/QAData' || path === '/Reports/ReceivalData') {
        return [...new Map(responses.flat().map(row => [row.id, row])).values()];
    }
    return { data: responses.flatMap(response => response.data) };
};

export const getLotReport = async (path, lots, params = {}, abortController) => {
    const uniqueLots = [...new Set(lots.filter(lot => typeof lot === 'string' && lot.trim()))];
    const batches = [];
    for (let index = 0; index < uniqueLots.length; index += 100) {
        batches.push(uniqueLots.slice(index, index + 100));
    }
    const responses = await mapWithConcurrency(batches, async lotSerialNbrs => {
        const response = await adalApiFetch(fetch, process.env.REACT_APP_BASE_URL + path, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ lotSerialNbrs, ...params }),
            signal: abortController?.signal
        });
        if (!response.ok) {
            throw new Error(`Traceability request failed (${response.status})`);
        }
        return response.json();
    }, 3, abortController?.signal);
    return mergeLotResponses(path, responses);
};